import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Download } from 'lucide-react';
import { canExportReports } from '@optiflow/shared';
import { useLocale } from '@/hooks/useLocale';
import { apiClient } from '@/services/apiClient';
import { useAuthStore } from '@/store/authStore';

type ExportTarget =
  { method: 'GET'; path: string; query: object } | { method: 'POST'; path: string; body: object };

/** Drops empty values and paging: the server decides how many rows an export may hold. */
function cleanQuery(query: object): Record<string, string | number> {
  return Object.fromEntries(
    Object.entries(query).filter(
      ([key, value]) =>
        value !== undefined &&
        value !== null &&
        value !== '' &&
        key !== 'page' &&
        key !== 'pageSize',
    ),
  ) as Record<string, string | number>;
}

/** The server names the file from fixed words and dates; a fallback is used only if the header is missing. */
function filenameOf(disposition: unknown): string {
  const match =
    typeof disposition === 'string' ? /filename="([a-z0-9-.]+)"/.exec(disposition) : null;
  return match?.[1] ?? 'optiflow-report.csv';
}

/** Reads a Blob's text through FileReader, which every browser and jsdom provide, unlike Blob.text(). */
function blobText(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(reader.error ?? new Error('Unreadable response.'));
    reader.readAsText(blob);
  });
}

/** A refused export arrives as JSON inside a blob; its code decides which sentence the reader sees. */
async function errorCode(error: unknown): Promise<string | null> {
  if (!axios.isAxiosError(error)) return null;
  const data: unknown = error.response?.data;
  try {
    const body: unknown =
      data instanceof Blob
        ? JSON.parse(await blobText(data))
        : typeof data === 'string'
          ? JSON.parse(data)
          : data;
    const code = (body as { error?: { code?: unknown } } | null)?.error?.code;
    return typeof code === 'string' ? code : null;
  } catch {
    return null;
  }
}

/**
 * Downloads a report as CSV. Shown only to an account with reporting.export; the server checks
 * that again, together with every permission the report itself needs, and builds the file from the
 * same record list the reader is looking at. The query goes as it is shown — nothing is added here
 * but the language of the column headers.
 */
export function ExportCsvButton({ target }: { target: ExportTarget }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const grants = useAuthStore((state) => state.user?.permissions);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  if (!canExportReports(grants)) return null;

  const run = async () => {
    setBusy(true);
    setProblem(null);
    try {
      const response =
        target.method === 'GET'
          ? await apiClient.get<Blob>(target.path, {
              params: { ...cleanQuery(target.query), locale },
              responseType: 'blob',
            })
          : await apiClient.post<Blob>(
              target.path,
              { ...target.body, locale },
              { responseType: 'blob' },
            );
      const url = URL.createObjectURL(response.data);
      const link = document.createElement('a');
      link.href = url;
      link.download = filenameOf(response.headers['content-disposition']);
      document.body.append(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (error) {
      const code = await errorCode(error);
      setProblem(
        code === 'EXPORT_TOO_LARGE'
          ? 'reports.export.tooLarge'
          : code === 'FORBIDDEN' || code === 'PERMISSION_DENIED'
            ? 'reports.export.forbidden'
            : 'reports.export.failed',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy}
        className="border-line text-ink hover:border-brand-300 inline-flex items-center gap-1.5 rounded-lg border bg-white px-3 py-1.5 text-sm font-medium disabled:opacity-60"
      >
        <Download className="size-4" aria-hidden />
        {busy ? t('reports.export.exporting') : t('reports.export.button')}
      </button>
      {problem ? (
        <span role="alert" className="order-last block basis-full text-xs text-red-700">
          {t(problem)}
        </span>
      ) : null}
    </>
  );
}
